<script>
  import { useForm, router, inertia } from '@inertiajs/svelte';
  import Icon from '@iconify/svelte';
  import AppLayout from '../../components/AppLayout.svelte';
  import Card from '../../components/ui/Card.svelte';
  import Field from '../../components/ui/Field.svelte';
  import Button from '../../components/ui/Button.svelte';
  import ConfirmDialog from '../../components/ui/ConfirmDialog.svelte';
  import PlatformBadge from '../../components/ui/PlatformBadge.svelte';
  import ImageUploader from '../../components/ui/ImageUploader.svelte';
  import MediaExpiryWarning from '../../components/ui/MediaExpiryWarning.svelte';

  let { bin, items = [], social_accounts = [] } = $props();

  let showAddForm = $state(false);
  let removing = $state(null);
  let uploadingCount = $state(0);

  const form = useForm({ body: '', social_account_ids: [], media: [] });

  function toggleAccount(id) {
    const ids = form.social_account_ids;
    form.social_account_ids = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
  }

  // Bumped on success to remount ImageUploader, which owns its own thumbnail
  // list — form.reset() only clears form.media, not the child's state.
  let uploaderKey = $state(0);

  function submit() {
    form
      .transform((data) => ({ post: data }))
      .post(`/bins/${bin.id}/bin_items`, {
        onSuccess: () => { form.reset(); uploaderKey += 1; showAddForm = false; },
      });
  }

  function move(item, direction) {
    const index = items.findIndex((i) => i.id === item.id);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    router.patch(`/bin_items/${item.id}`, { bin_item: { position: items[targetIndex].position } });
  }

  function confirmRemove(item) {
    removing = item;
  }

  function remove() {
    const item = removing;
    removing = null;
    router.delete(`/bin_items/${item.id}`);
  }
</script>

<AppLayout title={bin.name}>
  <div class="flex items-center justify-between mb-6">
    <a use:inertia href="/bins" class="text-sm text-secondary-500 hover:text-primary-600 flex items-center gap-1">
      <Icon icon="mdi:arrow-left" /> Back to bins
    </a>
    <div class="flex items-center gap-3">
      <Button href={`/bins/${bin.id}/edit`} variant="ghost"><Icon icon="mdi:pencil-outline" class="mr-1" /> Rename</Button>
      <Button onclick={() => { showAddForm = !showAddForm; }}><Icon icon="mdi:plus" class="mr-1" /> Add post</Button>
    </div>
  </div>

  <ConfirmDialog
    open={removing !== null}
    title="Remove this post from the bin?"
    body="The draft post will be deleted too. This can't be undone."
    confirmLabel="Remove"
    onconfirm={remove}
    oncancel={() => { removing = null; }}
  />

  {#if showAddForm}
    <div class="max-w-2xl mb-6">
      <Card title="Add a post to this bin">
        <form onsubmit={(e) => { e.preventDefault(); submit(); }} class="flex flex-col gap-5">
          <Field label="What do you want to say?" id="body" error={form.errors.body}>
            {#snippet children(describedBy)}
              <textarea
                id="body"
                rows="4"
                class="input resize-y"
                bind:value={form.body}
                maxlength="5000"
                placeholder="Write your post…"
                required
                aria-describedby={describedBy}
              ></textarea>
            {/snippet}
          </Field>

          <Field label="Images" id="images" error={form.errors.media}>
            {#snippet children(describedBy)}
              <div aria-describedby={describedBy}>
                {#key uploaderKey}
                  <ImageUploader bind:media={form.media} bind:uploading={uploadingCount} />
                {/key}
              </div>
            {/snippet}
            {#snippet hint()}
              <span class="text-xs text-secondary-400">
                Images expire 60 days after upload. Posts queued longer than that will need a re-upload.
              </span>
            {/snippet}
          </Field>

          <Field label="Post to" id="accounts" error={form.errors.social_account_ids}>
            <div class="flex flex-col gap-2">
              {#each social_accounts as acc}
                <label class="flex items-center gap-3 px-3 py-2 rounded-xl border border-secondary-200 cursor-pointer hover:bg-secondary-50 text-sm">
                  <input
                    type="checkbox"
                    checked={form.social_account_ids.includes(acc.id)}
                    onchange={() => toggleAccount(acc.id)}
                  />
                  <PlatformBadge platform={acc.platform} handle={acc.handle} />
                </label>
              {/each}
            </div>
          </Field>

          <div class="flex items-center gap-3">
            <Button type="submit" disabled={form.processing || uploadingCount > 0 || form.social_account_ids.length === 0}>
              {form.processing ? 'Adding…' : uploadingCount > 0 ? 'Uploading…' : 'Add to bin'}
            </Button>
            <Button variant="ghost" onclick={() => { showAddForm = false; }}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  {/if}

  {#if items.length === 0}
    <Card>
      <div class="text-center py-8">
        <Icon icon="mdi:tray-full" class="text-4xl text-secondary-300 mx-auto mb-3" />
        <p class="text-secondary-500 mb-4">This bin is empty. Add a post to queue it up.</p>
      </div>
    </Card>
  {:else}
    <div class="flex flex-col gap-3">
      {#each items as item, i (item.id)}
        <div class="card !p-5 flex items-start justify-between gap-4">
          <div class="min-w-0 flex gap-3">
            {#if item.post.media_count > 0}
              <div class="flex gap-1 shrink-0">
                {#each item.post.media.slice(0, 3) as m}
                  <div class="relative w-12 h-12 rounded-lg overflow-hidden border border-secondary-200 bg-secondary-50">
                    {#if m.url && !item.post.media_expired}
                      <img src={m.url} alt="" class="w-full h-full object-cover" />
                    {:else}
                      <div class="w-full h-full flex items-center justify-center text-secondary-300">
                        <Icon icon={item.post.media_expired ? 'mdi:image-off-outline' : 'mdi:image-outline'} />
                      </div>
                    {/if}
                  </div>
                {/each}
                {#if item.post.media_count > 3}
                  <div class="w-12 h-12 rounded-lg border border-secondary-200 flex items-center justify-center text-xs text-secondary-400">
                    +{item.post.media_count - 3}
                  </div>
                {/if}
              </div>
            {/if}
            <div class="min-w-0">
              <p class="text-secondary-800 mb-2">{item.post.body}</p>
              <div class="flex items-center gap-2 flex-wrap">
                {#each item.post.targets as t}
                  <PlatformBadge platform={t.platform} handle={t.handle} />
                {/each}
                <MediaExpiryWarning
                  expired={item.post.media_expired}
                  expiringSoon={item.post.media_expiring_soon}
                  expiresAt={item.post.media_expires_at}
                />
              </div>
            </div>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <button
              onclick={() => move(item, -1)}
              disabled={i === 0}
              aria-label="Move up"
              class="p-1.5 rounded-lg text-secondary-500 hover:bg-secondary-100 disabled:opacity-30"
            >
              <Icon icon="mdi:arrow-up" />
            </button>
            <button
              onclick={() => move(item, 1)}
              disabled={i === items.length - 1}
              aria-label="Move down"
              class="p-1.5 rounded-lg text-secondary-500 hover:bg-secondary-100 disabled:opacity-30"
            >
              <Icon icon="mdi:arrow-down" />
            </button>
            <button
              onclick={() => confirmRemove(item)}
              aria-label="Remove from bin"
              class="p-1.5 rounded-lg text-secondary-500 hover:bg-red-50 hover:text-red-700"
            >
              <Icon icon="mdi:close" />
            </button>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</AppLayout>
